import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Building2,
  Car,
  ChevronsUpDown,
  FileText,
  FolderKanban,
  Handshake,
  HeartHandshake,
  HelpCircle,
  KeyRound,
  LayoutDashboard,
  ReceiptText,
  Tags,
  TrendingUp,
  Users,
  UsersRound,
  Warehouse,
} from 'lucide-react';
import { PeriscopeLogo } from '@/components/brand/PeriscopeLogo';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSkeleton,
} from '@/components/ui/sidebar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ThemeToggle } from '@/components/theme/ThemeToggle';
import { UserMenu } from './UserMenu';
import { useCompaniesApi } from '@/services/api';
import { useAuth } from '@/hooks/useAuth';
import {
  hasCompanyAdminRole,
  hasCompanyRole,
  hasModuleAccess,
  landingSectionFor,
} from '@/lib/permissions';
import { CompanyLogo } from '@/components/companies/CompanyLogo';
import { t } from '@/i18n';
import type { CompanyListItem, PermissionModule } from '@ip/shared-types';

const SECTIONS = [
  { key: 'dashboard', icon: LayoutDashboard, labelKey: 'navigation.dashboard' },
  { key: 'invoices', icon: FileText, labelKey: 'navigation.invoices' },
  { key: 'sales', icon: TrendingUp, labelKey: 'navigation.sales' },
  { key: 'crm', icon: Handshake, labelKey: 'navigation.crm' },
  { key: 'fiscal', icon: ReceiptText, labelKey: 'navigation.fiscal' },
  { key: 'projects', icon: FolderKanban, labelKey: 'navigation.projects' },
  { key: 'contractors', icon: Users, labelKey: 'navigation.contractors' },
  { key: 'magazyn', icon: Warehouse, labelKey: 'navigation.magazyn' },
  { key: 'mileage', icon: Car, labelKey: 'navigation.mileage' },
  { key: 'employees', icon: UsersRound, labelKey: 'navigation.employees' },
  { key: 'hr', icon: HeartHandshake, labelKey: 'navigation.hr' },
  { key: 'tags', icon: Tags, labelKey: 'navigation.tags' },
  { key: 'documents', icon: FileText, labelKey: 'navigation.documents' },
] as const;

/**
 * Maps a sidebar section to the permission module that gates it — 'invoices'
 * is the 'expense' direction. 'projects' has its own module since 2026-07-13
 * (the "magazynier" grade): a user who only assigns invoices to projects must
 * not see the projects page/stats, so expense/sales no longer implies it.
 * 'documents' has NO permission module (spec 2026-07-16 — no new
 * PERMISSION_MODULE) — it's gated by role instead, as a special case in the
 * filter below, so its entry here is unreachable dead weight kept only to
 * satisfy this Record's completeness.
 */
const SECTION_MODULE: Record<(typeof SECTIONS)[number]['key'], PermissionModule> = {
  dashboard: 'dashboard',
  invoices: 'expense',
  sales: 'sales',
  crm: 'crm',
  fiscal: 'fiscal',
  projects: 'projects',
  contractors: 'contractors',
  magazyn: 'magazyn',
  mileage: 'mileage',
  employees: 'employees',
  hr: 'hr',
  tags: 'tags',
  documents: 'hr',
};

// Sections that don't apply to the 'other' (Unassigned) pseudo-company —
// there's no real company record to attach employees, mileage, sales, HR,
// project or document-template data to.
const NOT_APPLICABLE_TO_OTHER = new Set(['mileage', 'employees', 'sales', 'hr', 'projects', 'documents', 'fiscal', 'magazyn', 'crm']);

export function AppSidebar() {
  const [companies, setCompanies] = useState<CompanyListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const { getCompanies } = useCompaniesApi();
  const { isSuperAdmin, permissions } = useAuth();
  const canManageUsers = isSuperAdmin || hasCompanyAdminRole(permissions);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    let mounted = true;
    getCompanies()
      .then((data) => {
        if (mounted) setCompanies(data);
      })
      .catch((error) => {
        console.error('Failed to load companies:', error);
        toast.error(t('errors.loadingFailed'));
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
    // Refetch on navigation too: the admin Companies view can create/delete
    // companies, and the server-side list cache is invalidated on those
    // mutations, so re-running this on every route change is cheap and keeps
    // the switcher fresh after leaving /admin/companies.
  }, [getCompanies, location.pathname]);

  const { activeNip, activeSection } = useMemo(() => {
    const match = location.pathname.match(
      /^\/([^/]+)\/(dashboard|invoices|sales|crm|fiscal|projects|contractors|magazyn|mileage|employees|hr|tags|documents)/
    );
    if (match) return { activeNip: match[1], activeSection: match[2] };
    return { activeNip: null, activeSection: null };
  }, [location.pathname]);

  const activeCompany = companies.find((c) => c.nip === activeNip) ?? null;

  // GET /companies already returns only companies the caller can see at all
  // (backend-scoped) — this is a defensive client-side guard, not the
  // primary control, per the permission-driven-navigation requirement.
  const visibleCompanies = useMemo(() => {
    if (!permissions || permissions.isSuperAdmin) return companies;
    return companies.filter((c) => c.nip in permissions.companies);
  }, [companies, permissions]);

  const companyLabel = (company: CompanyListItem) =>
    company.nip === 'other' ? t('navigation.unassigned') : company.name;

  /**
   * Company avatar for the switcher: the uploaded logo when present, otherwise
   * the generic icon. The logo's plate (dark vs white) follows the logo's own
   * ink tone — see CompanyLogo — so a white logo (FSS) and a black one both
   * read correctly in the light AND the dark theme.
   */
  const CompanyAvatar = ({
    company,
    className = 'size-8',
  }: {
    company: CompanyListItem | null;
    className?: string;
  }) => {
    if (company?.logoUrl) {
      return (
        <CompanyLogo
          src={company.logoUrl}
          alt={companyLabel(company)}
          ink={company.logoInk}
          className={className}
        />
      );
    }
    return (
      <div
        className={`bg-sidebar-primary text-sidebar-primary-foreground flex aspect-square ${className} shrink-0 items-center justify-center rounded-lg`}
      >
        {!company ? (
          <PeriscopeLogo className="size-4" />
        ) : company.nip === 'other' ? (
          <HelpCircle className="size-4" />
        ) : (
          <Building2 className="size-4" />
        )}
      </div>
    );
  };

  const badgeFor = (company: CompanyListItem, section: string) => {
    if (section === 'invoices') return company.monthlyInvoiceCount;
    if (section === 'contractors') return company.contractorCount;
    return 0;
  };

  /**
   * Whether `section` exists AND is permitted for the user in `company` —
   * the same predicate the sidebar section list is filtered by, so the
   * switcher can never send the user somewhere the sidebar wouldn't show.
   */
  const sectionAvailableIn = (company: CompanyListItem, section: string): boolean => {
    if (company.nip === 'other' && NOT_APPLICABLE_TO_OTHER.has(section)) return false;
    // Documents is role-gated, not module-gated — mirror the section filter.
    // null permissions fail OPEN like hasModuleAccess does (legacy session /
    // /auth/me not yet loaded): the backend still authorizes every request.
    if (section === 'documents') {
      return (
        isSuperAdmin ||
        permissions === null ||
        hasCompanyRole(permissions, company.nip, ['admin', 'hr', 'ksiegowy'])
      );
    }
    return hasModuleAccess(
      permissions,
      company.nip,
      SECTION_MODULE[section as keyof typeof SECTION_MODULE],
    );
  };

  /**
   * Destination when switching to `company`: keep the current section only
   * if the user may see it THERE; otherwise fall back to the permission-aware
   * landing section (never a hard-coded 'invoices' — a `pracownik` with only
   * the 'hr' module must land on /:nip/hr, not on the expense list).
   */
  const switchTargetFor = (company: CompanyListItem): string => {
    if (activeSection && sectionAvailableIn(company, activeSection)) return activeSection;
    return landingSectionFor(isSuperAdmin, permissions, company.nip);
  };

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton
                  size="lg"
                  className="data-[state=open]:bg-sidebar-accent"
                >
                  <CompanyAvatar company={activeCompany} />
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-semibold">
                      {activeCompany ? companyLabel(activeCompany) : t('app.name')}
                    </span>
                    {activeCompany && activeCompany.nip !== 'other' && (
                      <span className="text-muted-foreground truncate font-mono text-xs">
                        NIP {activeCompany.nip}
                      </span>
                    )}
                  </div>
                  <ChevronsUpDown className="ml-auto size-4" />
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                className="w-(--radix-dropdown-menu-trigger-width) min-w-56"
                align="start"
              >
                <DropdownMenuLabel className="text-muted-foreground text-xs">
                  {t('navigation.companies')}
                </DropdownMenuLabel>
                {visibleCompanies.map((company) => (
                  <DropdownMenuItem
                    key={company.nip}
                    onClick={() =>
                      navigate(`/${company.nip}/${switchTargetFor(company)}`)
                    }
                  >
                    <CompanyAvatar company={company} className="size-6" />
                    <span className="truncate">{companyLabel(company)}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        {loading && (
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu>
                {Array.from({ length: 5 }, (_, i) => (
                  <SidebarMenuItem key={i}>
                    <SidebarMenuSkeleton showIcon />
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        {activeCompany && (
          <SidebarGroup>
            <SidebarGroupLabel>
              <span className="truncate" title={companyLabel(activeCompany)}>
                {companyLabel(activeCompany)}
              </span>
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {SECTIONS.filter((section) => {
                  if (activeCompany.nip === 'other' && NOT_APPLICABLE_TO_OTHER.has(section.key)) {
                    return false;
                  }
                  // Documents has no permission module (spec 2026-07-16) —
                  // role-gated instead: hr/admin for all categories,
                  // ksiegowy additionally for the contractor category only
                  // (the backend filters that list; this just decides
                  // whether the sidebar entry shows at all).
                  if (section.key === 'documents') {
                    return isSuperAdmin || hasCompanyRole(permissions, activeCompany.nip, ['admin', 'hr', 'ksiegowy']);
                  }
                  return hasModuleAccess(permissions, activeCompany.nip, SECTION_MODULE[section.key]);
                }).map((section) => {
                  const count = badgeFor(activeCompany, section.key);
                  return (
                    <SidebarMenuItem key={section.key}>
                      <SidebarMenuButton
                        isActive={activeSection === section.key}
                        onClick={() =>
                          navigate(`/${activeCompany.nip}/${section.key}`)
                        }
                        tooltip={t(section.labelKey)}
                      >
                        <section.icon />
                        <span>{t(section.labelKey)}</span>
                      </SidebarMenuButton>
                      {count > 0 && <SidebarMenuBadge>{count}</SidebarMenuBadge>}
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        {(isSuperAdmin || canManageUsers) && (
          <SidebarGroup>
            <SidebarGroupLabel>{t('navigation.administration')}</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {isSuperAdmin && (
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      isActive={location.pathname.startsWith('/admin/companies')}
                      onClick={() => navigate('/admin/companies')}
                      tooltip={t('navigation.companies')}
                    >
                      <Building2 />
                      <span>{t('navigation.companies')}</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                )}
                {isSuperAdmin && (
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      isActive={location.pathname.startsWith('/admin/api-apps')}
                      onClick={() => navigate('/admin/api-apps')}
                      tooltip={t('navigation.apiApps')}
                    >
                      <KeyRound />
                      <span>{t('navigation.apiApps')}</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                )}
                {canManageUsers && (
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      isActive={location.pathname.startsWith('/admin/users')}
                      onClick={() => navigate('/admin/users')}
                      tooltip={t('navigation.users')}
                    >
                      <Users />
                      <span>{t('navigation.users')}</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                )}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
      </SidebarContent>

      <SidebarFooter>
        <div className="flex items-center gap-1 group-data-[collapsible=icon]:hidden">
          <div className="flex-1">
            <UserMenu />
          </div>
          <ThemeToggle />
        </div>
      </SidebarFooter>
    </Sidebar>
  );
}
